import React, { useState, useEffect, useRef } from "react";
import {
  LightModeOutlined,
  DarkModeOutlined,
  ArrowDropDownOutlined,
  Star as StarIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Typography,
  IconButton,
  Drawer,
  useTheme,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { setMode } from "state";
import { logout } from "state/authSlice";
import { useNavigate } from "react-router-dom";
import FlexBetween from "./FlexBetween";
import ScoreDashboard from "./ScoreDashboard";
import { useGetBestScorersQuery, useUpdatepasswordMutation } from "state/api";


const Navbar = ({ isSidebarOpen, setIsSidebarOpen }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();

  const { username, MgCd, EmpNo } = useSelector((state) => state.global);

  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);
  const passwordResetRef = useRef(null); // Reference for the password reset section

  const [updatePassword] = useUpdatepasswordMutation();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target) &&
        (!passwordResetRef.current || !passwordResetRef.current.contains(event.target))
      ) {
        setShowProfileMenu(false);
        setIsUpdatingPassword(false); // Close password reset if clicking outside
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/LoginPage");
  };

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  const handlePasswordReset = async () => {
    setPasswordError("");

    if (!newPassword || !confirmPassword) {
      return setPasswordError("Please enter both new password fields.");
    }

    if (newPassword !== confirmPassword) {
      return setPasswordError("Passwords do not match.");
    }

    try {
      const res = await updatePassword({ EmpNo, newpassword: newPassword }).unwrap();
      alert(res.message || "Password updated successfully.");
      setNewPassword("");
      setConfirmPassword("");
      setIsUpdatingPassword(false);
    } catch (err) {
      alert("Failed to update password.");
    }
  };

  const [isStarSidebarOpen, setIsStarSidebarOpen] = useState(false);
  const { data: bestScorers } = useGetBestScorersQuery();

  const toggleStarSidebar = () => {
    setIsStarSidebarOpen((prev) => !prev);
    if (showProfileMenu) {
      setShowProfileMenu(false); // Close profile menu if it's open
    }
  };

  const toggleProfileMenu = () => {
    setShowProfileMenu((prev) => !prev);
    if (isStarSidebarOpen) {
      setIsStarSidebarOpen(false); // Close star sidebar if it's open
    }
  };

  return (
    <>
      <AppBar
        position="static"
        sx={{ background: theme.palette.background.default, boxShadow: "none", zIndex: 1300 }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            backgroundColor: "transparent",
            backdropFilter: "blur(50px)",
            padding: "0 20px",
          }}
        >
          {/* LEFT - Star Performer */}
          <Button
            onClick={toggleStarSidebar} // Toggle sidebar
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              textTransform: "none",
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.common.white,
              "&:hover": { backgroundColor: theme.palette.primary.dark },
              borderRadius: "20px",
              padding: "8px 16px",
            }}
          >
            <StarIcon sx={{ color: "yellow", fontSize: "20px" }} />
            <span>Star Performer</span>
          </Button>

          {/* RIGHT - Mode Toggle & Profile */}
          <FlexBetween gap="1.5rem">
            <IconButton onClick={() => dispatch(setMode())}>
              {theme.palette.mode === "dark" ? (
                <DarkModeOutlined sx={{ fontSize: "25px" }} />
              ) : (
                <LightModeOutlined sx={{ fontSize: "25px" }} />
              )}
            </IconButton>

            <Box sx={{ position: "relative", zIndex: 9999 }}>
              <Button
                variant="contained"
                onClick={toggleProfileMenu} // Toggle profile menu
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.common.white,
                  "&:hover": { backgroundColor: theme.palette.primary.dark },
                  borderRadius: "20px",
                  padding: "8px 16px",
                }}
              >
                Profile <ArrowDropDownOutlined />
              </Button>

              {showProfileMenu && (
                <Box
                  ref={profileMenuRef}
                  sx={{
                    position: "absolute",
                    top: "60px",
                    right: 0,
                    backgroundColor: theme.palette.background.default,
                    boxShadow: "0px 2px 10px rgba(0,0,0,0)",
                    borderRadius: "8px",
                    padding: "16px",
                    zIndex: 9999,
                    width: 250,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    Hello, {username.trim().split(" ")[0] || "User    "}   🤵‍♂️
                  </Typography>

                  <Box mt={2} ref={passwordResetRef}> {/* Reference for password reset section */}
                    {!isUpdatingPassword ? (
                      <>
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={() => setIsUpdatingPassword(true)}
                          color="#000000"
                          sx={{ marginBottom: "10px", color: theme.palette.common.white, "&:hover": { backgroundColor: theme.palette.primary.dark }, }}
                        >
                          Reset Password
                        </Button>
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={handleLogout}
                          sx={{ marginBottom: "10px", color: theme.palette.common.white, "&:hover": { backgroundColor: theme.palette.primary.dark }, }}
                          color="#000000"  
                        >
                          Logout
                        </Button>
                      </>
                    ) : (
                      <>
                        <input
                          type="password"
                          placeholder="New Password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          style={{
                            width: "100%",
                            padding: "8px 10px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            marginBottom: "10px",
                            outline: "none",
                            transition: "border-color 0.3s",
                          }}
                          onFocus={(e) => (e.target.style.borderColor = theme.palette.primary.main)}
                          onBlur={(e) => (e.target.style.borderColor = "#ccc")}
                        />
                        <input
                          type="password"
                          placeholder="Retype New Password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          style={{
                            width: "100%",
                            padding: "8px 10px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            marginBottom: "10px",
                            outline: "none",
                            transition: "border-color 0.3s",
                          }}
                          onFocus={(e) => (e.target.style.borderColor = theme.palette.primary.main)}
                          onBlur={(e) => (e.target.style.borderColor = "#ccc")}
                        />
                        {passwordError && (
                          <Typography color="error" variant="caption">
                            {passwordError}
                          </Typography>
                        )}
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={handlePasswordReset}
                          sx={{
                            backgroundColor: theme.palette.secondary.main,
                            color: theme.palette.common.white,
                            mb: 1,
                            "&:hover": {
                              backgroundColor: theme.palette.secondary.dark,
                            },
                          }}
                        >
                          Reset
                        </Button>
                      </>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </FlexBetween>
        </Toolbar>
      </AppBar>

      {/* Drawer for Star Performer */}
      <Drawer
  anchor="left"
  open={isStarSidebarOpen}
  onClose={() => setIsStarSidebarOpen(false)}
>
  <Box
    sx={{
      width: 300,
      backgroundColor: theme.palette.background.default,
      height: "100vh",
      padding: "20px",
    }}
  >
    <FlexBetween>
      <Typography variant="h6" fontWeight="bold">
        ⭐ Star Performers
      </Typography>
      <IconButton onClick={() => setIsStarSidebarOpen(false)}>
        <CloseIcon />
      </IconButton>
    </FlexBetween>

    
    <ScoreDashboard />
  </Box>
</Drawer>

    </>
  );
};

export default Navbar;  